/**
 * Sets a timeout with the provided callback function and delay.
 * @param onTimeout The callback function to execute when the timeout elapses.
 * @param delay The delay in milliseconds before the timeout elapses.
 * @returns A reference to the timeout, which can be used to clear the timeout later.
 */
export interface TimeoutController {
  set: (onTimeout: () => void, delay: number) => void;
  clear: () => void;
}

/**
 * Why do we create safe timeout ?
 * Browsers including Internet Explorer, Chrome, Safari, and Firefox store the delay as a
 * 32-bit signed integer internally. This causes an integer overflow when using delays larger
 * than 2,147,483,647 ms (about 24.8 days), resulting in the timeout being executed immediately.
 */

const MAX_32_BIT = 0x7fffffff;

/**
 * Clears the specified timeout.
 * @param timeoutId The ID of the timeout to clear.
 */
export function createSafeTimeout(): TimeoutController {
  let timeoutId: any = null;

  function set(onTimeout: () => void, delay: number): void {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(
      () => {
        if (delay > MAX_32_BIT) {
          set(onTimeout, delay - MAX_32_BIT);
        } else {
          onTimeout();
        }
      },
      Math.min(MAX_32_BIT, delay),
    );
  }

  function clear(): void {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  }

  return { set, clear };
}
